import React, { forwardRef, Ref, useEffect } from 'react';
import { ImageStyle, Image as ReactNativeImage } from 'react-native';

import { Radius } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { GetProps, isWebTouchable, styled, usePropsAndStyle } from '@tamagui/core';

import { AnalyticsProps, ComponentWithViewTracking } from '../../helpers/analytics';
import { getBorderRadiusByVariant } from './helpers';

type ImageRadius = Exclude<
  Radius,
  '$3x' | '$4x' | '$13x' | '$14x' | '$32x' | '$60x' | 'borderAllowance'
>;
export type ImageVariant = 'arch' | 'circle' | 'article';
type ImageOrientation = 'top' | 'bottom' | 'right' | 'left';
const BaseImageReactNative = styled(ReactNativeImage, {
  name: 'Image',
  variants: {
    variant: (variant: ImageVariant, extras) => {
      const props = extras.props as BaseImageProps;
      const orientation = props.orientation || '';
      return getBorderRadiusByVariant(variant, orientation);
    },
  } as const,
});
type BaseImageProps = {
  /**
   * This adds orientation of the image
   * 'top' | 'bottom' | 'right' | 'left'
   */
  orientation?: ImageOrientation;
};

type BaseImageReactNativeProps = GetProps<typeof BaseImageReactNative>;

type PinchStart = {
  /**
   * X coordinate of the center of the two fingers
   */
  x: number;
  /**
   * Y coordinate of the center of the two fingers
   */
  y: number;
  /**
   * Distance between the two fingers
   */
  distance: number;
};

type CrossBrowserTouchEvent = TouchEvent & {
  /**
   * Scale of the image, provided by Safari but not yet part of the TouchEvent type
   */
  scale?: number;
};

// Define ImageProps with conditional orientation requirement
export type ImageProps = Partial<AnalyticsProps> &
  Omit<
    BaseImageReactNativeProps,
    | 'borderRadius'
    | 'borderBottomLeftRadius'
    | 'borderBottomRightRadius'
    | 'borderTopRightRadius'
    | 'borderTopLeftRadius'
    | 'alt'
  > & {
    /**
     * Threshold for impression tracking. 0.5 means 50% of the box must be in view to trigger impression
     * Default: 1 (100%)
     */
    inViewThreshold?: number;
    /**
     * This adds borderRadius styling to the Image (works for mobile & web)
     */
    borderRadius?: ImageRadius;
    /**
     * This adds borderBottomLeftRadius styling to the Image (works for mobile & web)
     */
    borderBottomLeftRadius?: ImageRadius;
    /**
     * This adds borderBottomRightRadius styling to the Image (works for mobile & web)
     */
    borderBottomRightRadius?: ImageRadius;
    /**
     * This adds borderTopRightRadius styling to the Image (works for mobile & web)
     */
    borderTopRightRadius?: ImageRadius;
    /**
     * This adds borderTopLeftRadius styling to the Image (works for mobile & web)
     */
    borderTopLeftRadius?: ImageRadius;
    /**
     * This adds alt for the image
     */
    alt: string;
    /**
     * Enables zoom
     */
    enableZoom?: boolean;
  } & ( // Orientation is required if variant is 'article'
    | {
        /**
         * variant for the image
         */
        variant: 'article';
        /**
         * orientation for the image
         */
        orientation: ImageOrientation;
      }
    // Orientation is forbidden if variant is not 'article'
    | {
        /**
         * variant for the image
         */
        variant?: Exclude<ImageVariant, 'article'>;
        /**
         * orientation for the image
         */
        orientation?: never;
      }
  );

/**
 * Image is an abstract styled component that renders a `<img>` element on web.
 * Some important things to note:
 * - The `source` prop is required. Typically, this will be an object that
 *   includes a `uri` pointing to a remote image asset. In other cases,
 *   `source` could be a local image asset that gets "required" into the bundle.
 * - `source.width` and `source.height` refer to the dimensions of the image
 *   resource, not the rendered image. To control the rendered image size, use
 *   the `width` and `height` props.
 * - Orientation is required if `variant` is "article"
 * - If `variant` is "circle", the rendered height will be the same as the
 *   rendered width (the `height` prop will be ignored)
 * - Image does not support press handlers like `onPress`. For a11y reasons, you should only use interactive components like Button to handle press interactions.
 */
export const Image = forwardRef(function Image(
  {
    analyticsName,
    trackImpression = false,
    inViewThreshold,
    source,
    enableZoom = false,
    ...inProps
  }: ImageProps,
  ref: Ref<ReactNativeImage>,
) {
  if (inProps.variant === 'circle') {
    inProps.height = inProps.width;
  }
  // Workaround for react-native-web Image setting width and height in the
  // rendered element's style attribute. Compare with how tamagui solves this:
  // https://github.com/tamagui/tamagui/blob/master/packages/image/src/Image.tsx#L53
  const [props, style] = usePropsAndStyle(inProps);

  // Handle pinch to zoom on web
  useEffect(() => {
    const imageRef = (isWebTouchable && ref && 'current' in ref ? ref.current : undefined) as
      | HTMLImageElement
      | undefined;
    if (isWebTouchable && imageRef && enableZoom) {
      let imageElementScale = 1;
      const SCALE_MAX = 4;
      const start: PinchStart = { x: 0, y: 0, distance: 0 };

      // Calculate distance between two fingers
      const distance = (event: CrossBrowserTouchEvent) => {
        return Math.hypot(
          event.touches[0].pageX - event.touches[1].pageX,
          event.touches[0].pageY - event.touches[1].pageY,
        );
      };

      const handleTouchStart = (event: CrossBrowserTouchEvent) => {
        if (event.touches.length === 2) {
          event.preventDefault(); // Prevent page scroll

          // Calculate where the fingers have started on the X and Y axis
          start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
          start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
          start.distance = distance(event);
        }
      };

      const handleTouchMove = (event: CrossBrowserTouchEvent) => {
        if (event.touches.length === 2) {
          event.preventDefault(); // Prevent page scroll

          // Safari provides event.scale as two fingers move on the screen
          // For other browsers just calculate the scale manually
          let scale;
          if (event?.scale) {
            scale = event.scale;
          } else {
            const deltaDistance = distance(event);
            scale = deltaDistance / start.distance;
          }
          imageElementScale = Math.min(Math.max(1, scale), SCALE_MAX);

          // Calculate how much the fingers have moved on the X and Y axis
          const deltaX = ((event.touches[0].pageX + event.touches[1].pageX) / 2 - start.x) * 2; // x2 for accelarated movement
          const deltaY = ((event.touches[0].pageY + event.touches[1].pageY) / 2 - start.y) * 2; // x2 for accelarated movement

          // Transform the image to make it grow and move with fingers
          const transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${imageElementScale})`;
          imageRef.style.transform = transform;
          imageRef.style.webkitTransform = transform;
          imageRef.style.zIndex = '9999';
          imageRef.style.transition = 'ease 150ms';
        }
      };

      const handleTouchEnd = () => {
        // Reset image to it's original format
        imageRef.style.transform = '';
        imageRef.style.webkitTransform = '';
        imageRef.style.zIndex = '';
      };

      imageRef?.addEventListener('touchstart', handleTouchStart);
      imageRef?.addEventListener('touchmove', handleTouchMove);
      imageRef?.addEventListener('touchend', handleTouchEnd);

      return () => {
        imageRef?.removeEventListener('touchstart', handleTouchStart);
        imageRef?.removeEventListener('touchmove', handleTouchMove);
        imageRef?.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [ref, enableZoom]);

  const BaseImageWithProps = (
    <BaseImageReactNative
      {...props}
      accessibilityLabel={props.alt}
      source={source}
      style={style as ImageStyle}
      ref={ref}
    />
  );

  if (analyticsName && trackImpression) {
    return (
      <ComponentWithViewTracking
        Component={BaseImageWithProps}
        analyticsName={analyticsName}
        eventName={'imageViewEvent'}
        inViewThreshold={inViewThreshold}
        ref={ref}
        {...props}
      />
    );
  }

  return BaseImageWithProps;
});
